import { computeHistogramGeometry, computeValueAxis, decimateIndices } from './graph-geometry.js';
import { formatUnits } from './format.js';
import type { ResourceGraphProps } from './types.js';

/**
 * 資源長條圖（Resource Graph / Histogram）：每個時段的分配量對比可用量。
 * 超過可用量的部分為紅色，未超過為藍色；可用量畫成水平參考線。
 * 附 X 軸日期刻度、Y 軸百分比刻度與淡色格線，供使用者判讀「超額發生在哪幾天、超了多少」。
 * 純 SVG 自繪，不引入圖表套件。
 */

const MARGIN = { left: 44, right: 8, top: 8, bottom: 20 };
const MIN_X_LABEL_WIDTH = 40;

export function ResourceGraph(props: ResourceGraphProps): React.JSX.Element {
  const { series, width = 640, heightPerSeries = 160 } = props;
  const plotWidth = Math.max(1, width - MARGIN.left - MARGIN.right);
  const plotHeight = Math.max(1, heightPerSeries - MARGIN.top - MARGIN.bottom);

  return (
    <div data-testid="resource-graph" className="flex w-full flex-col gap-4 bg-surface p-2 text-sm text-fg">
      {series.map((s) => {
        const dataMax = s.points.reduce((m, p) => Math.max(m, p.allocatedUnits), 0);
        const yAxis = computeValueAxis(Math.max(dataMax, s.maxUnits), 4);
        const geometry = computeHistogramGeometry(
          s.points,
          s.maxUnits,
          plotWidth,
          plotHeight,
          yAxis.niceMax > 0 ? yAxis.niceMax : undefined,
        );
        const labelIndices = new Set(decimateIndices(s.points.length, plotWidth, MIN_X_LABEL_WIDTH));

        return (
          <div key={s.resourceId} data-testid={`resource-graph-series-${s.resourceId}`}>
            <div className="mb-1 font-medium">
              {s.resourceName}
              <span className="ml-2 text-xs text-fg-muted">
                可用量 {formatUnits(s.maxUnits)}
              </span>
            </div>
            <svg
              role="img"
              aria-label={`${s.resourceName} 資源長條圖`}
              width={width}
              height={heightPerSeries}
              viewBox={`0 0 ${width} ${heightPerSeries}`}
              className="overflow-visible"
            >
              {/* Y 軸刻度、格線與標籤 */}
              {yAxis.ticks.map((tick) => {
                const y = MARGIN.top + plotHeight - tick.ratio * plotHeight;
                return (
                  <g key={tick.value} data-testid={`resource-graph-ytick-${s.resourceId}-${tick.value}`}>
                    <line
                      x1={MARGIN.left}
                      x2={MARGIN.left + plotWidth}
                      y1={y}
                      y2={y}
                      className="stroke-hairline"
                      strokeWidth={1}
                    />
                    <text
                      x={MARGIN.left - 6}
                      y={y}
                      dominantBaseline="middle"
                      textAnchor="end"
                      className="fill-fg-muted text-[10px]"
                    >
                      {formatUnits(tick.value)}
                    </text>
                  </g>
                );
              })}

              {/* X 軸日期標籤（防重疊：只顯示 decimateIndices 挑出的索引） */}
              {s.points.map((point, index) =>
                labelIndices.has(index) ? (
                  <text
                    key={point.periodStart}
                    data-testid={`resource-graph-xtick-${s.resourceId}-${index}`}
                    x={MARGIN.left + (plotWidth / Math.max(s.points.length, 1)) * (index + 0.5)}
                    y={MARGIN.top + plotHeight + 14}
                    textAnchor="middle"
                    className="fill-fg-muted text-[10px]"
                  >
                    {point.label}
                  </text>
                ) : null,
              )}

              <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
                <line
                  data-testid={`resource-graph-refline-${s.resourceId}`}
                  x1={0}
                  x2={plotWidth}
                  y1={geometry.referenceLineY}
                  y2={geometry.referenceLineY}
                  className="stroke-line-strong"
                  strokeDasharray="4 2"
                />
                {geometry.bars.map((bar, index) => {
                  const point = s.points[index];
                  return (
                    <g key={point?.periodStart ?? index} data-testid={`resource-graph-bar-${s.resourceId}-${index}`}>
                      {bar.normalHeight > 0 ? (
                        <rect
                          data-testid={`resource-graph-bar-normal-${s.resourceId}-${index}`}
                          x={bar.x}
                          y={bar.y + bar.overHeight}
                          width={bar.width}
                          height={bar.normalHeight}
                          className="fill-focus"
                        />
                      ) : null}
                      {bar.overHeight > 0 ? (
                        <rect
                          data-testid={`resource-graph-bar-over-${s.resourceId}-${index}`}
                          x={bar.x}
                          y={bar.y}
                          width={bar.width}
                          height={bar.overHeight}
                          className="fill-danger-bar"
                        />
                      ) : null}
                    </g>
                  );
                })}
              </g>

              {/* 軸線 */}
              <line
                x1={MARGIN.left}
                x2={MARGIN.left}
                y1={MARGIN.top}
                y2={MARGIN.top + plotHeight}
                className="stroke-line-strong"
              />
              <line
                x1={MARGIN.left}
                x2={MARGIN.left + plotWidth}
                y1={MARGIN.top + plotHeight}
                y2={MARGIN.top + plotHeight}
                className="stroke-line-strong"
              />
            </svg>
          </div>
        );
      })}
    </div>
  );
}

export default ResourceGraph;
